/**
 * ClinicianRecordDrawer.tsx — admin edit of a clinician's prescribing record (G2).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: G2 (confirmations + audit).
 *
 * WHY: a clinician's fullName / registrationNumber / prescriberType end up on
 * every prescription PDF, so changing them is an admin-only, audited
 * operation. Opened from the Users tab's per-row ⋯ menu (clinician rows
 * only), this Drawer (lg, frozen header) shows the record and edits all three
 * fields plus an OPTIONAL reason that is recorded in the identity audit log
 * (R19: the reason lives only on the audit row — never in an event/log).
 *
 * A registrationNumber change SPECIFICALLY gets a second, danger-toned
 * ConfirmDialog before the PATCH — it is the prescribing identity on the PDF,
 * so a silent typo-edit would be the worst kind of quiet failure. Name/type
 * edits save directly.
 */
import { useState, type FormEvent } from 'react';
import { updateClinicianRecord } from '../api/identity';
import type { PrescriberType, StaffUserRow } from '../api/types';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer, Button, ErrorLine, Field, Help, Select, TextArea, TextInput } from '../ui/kit';
import { Row } from '../staff/ui';

const PRESCRIBER_TYPES: Array<{ value: PrescriberType; label: string }> = [
  { value: 'doctor', label: 'Doctor' },
  { value: 'independent_prescriber', label: 'Independent prescriber' },
  { value: 'pharmacist', label: 'Pharmacist' },
];

export function ClinicianRecordDrawer({ user, onClose, onSaved }: { user: StaffUserRow; onClose: () => void; onSaved: () => void }) {
  const [fullName, setFullName] = useState(user.fullName ?? '');
  const [registrationNumber, setRegistrationNumber] = useState(user.registrationNumber ?? '');
  const [prescriberType, setPrescriberType] = useState<PrescriberType | ''>(user.prescriberType ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRegChange, setConfirmingRegChange] = useState(false);

  const regChanged = registrationNumber.trim() !== (user.registrationNumber ?? '');
  const valid = fullName.trim() !== '' && registrationNumber.trim() !== '';

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const trimmedReason = reason.trim();
      await updateClinicianRecord(user.userId, {
        fullName: fullName.trim(),
        registrationNumber: registrationNumber.trim(),
        ...(prescriberType ? { prescriberType } : {}),
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      });
      onSaved();
      onClose();
    } catch {
      setError('Sorry — we could not save the clinician record. Please try again.');
      setBusy(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy || !valid) return;
    // The registration number is the prescribing identity on the PDF — a
    // change to IT specifically gets the second, danger-toned confirmation.
    if (regChanged) {
      setConfirmingRegChange(true);
      return;
    }
    void save();
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Clinician record — ${user.fullName || user.email}`}
      width="lg"
      footer={
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit" form="clinician-record-form" variant="cta" disabled={busy || !valid} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
            {busy ? 'Saving…' : 'Save record'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
            Cancel
          </Button>
        </div>
      }
    >
      <Row label="Email" value={user.email} />
      <Row label="Role" value={user.role} />
      <Help style={{ margin: 'var(--gps-s-3) 0' }}>
        These details appear on every prescription this clinician issues. Changes are recorded in the audit log.
      </Help>

      <form id="clinician-record-form" onSubmit={handleSubmit} aria-label="Edit clinician record">
        <Field label="Full name" htmlFor="record-full-name">
          <TextInput id="record-full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <Field label="Registration number" htmlFor="record-registration">
          <TextInput id="record-registration" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
        </Field>
        <Field label="Prescriber type" htmlFor="record-prescriber-type">
          <Select id="record-prescriber-type" value={prescriberType} onChange={(e) => setPrescriberType(e.target.value as PrescriberType | '')}>
            <option value="">Not set</option>
            {PRESCRIBER_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Reason for change (recorded in the audit log)" htmlFor="record-reason" hint="Optional.">
          <TextArea id="record-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {error ? <ErrorLine>{error}</ErrorLine> : null}
      </form>

      {confirmingRegChange ? (
        <ConfirmDialog
          open
          title="Change the registration number?"
          tone="danger"
          body={`This changes the prescribing identity on every prescription this clinician issues — from "${user.registrationNumber || '—'}" to "${registrationNumber.trim()}". Double-check it against the register before confirming.`}
          confirmLabel="Change registration number"
          busy={busy}
          onConfirm={() => {
            setConfirmingRegChange(false);
            void save();
          }}
          onCancel={() => setConfirmingRegChange(false)}
        />
      ) : null}
    </Drawer>
  );
}
